import React from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react";

import Page from "@app/components/page/page";
import Header from "@app/components/setup/header";
import Reasons from "@app/components/setup/reason/group";
import ReviewToggles from "@app/components/setup/score/toggles";
import ScoreBands from "@app/components/setup/score/bands";
import AdditionalDataGroup from "@app/components/setup/additionalData/group";
import Questions from "@app/components/setup/question/group";
import SlrToggle from "@app/components/setup/slr/toggle";
import notify from "@app/components/notify";
import Keywords from "@app/components/setup/keywords/group";

import setup from "@app/state/store/projects/setup";
import report from "@app/state/store/report";
import session from "@app/state/store/session";

const SLR = observer(() => {
    const history = useHistory();
    const config = setup.config?.slr;
    const project = setup.project;
    const projectManage = session.can("project.manage");
    const readonly = report.readOnly || setup.isComplete;

    if (!config || !project) {
        return;
    }

    const close = () => {
        try {
            history.push(`/r/${project._id}/setup`);
        } catch (error) {
            notify.error("Error navigating back to setup page");
        }
    };

    const update = async (updates, level, args = {}) => {
        if (!config) {
            notify.error("Configuration error");
            return;
        }

        try {
            let result = {};

            if (level) {
                result[level] = {
                    ...config[level],
                    ...updates,
                };
            } else {
                result = {
                    ...config,
                    ...updates,
                };
            }

            await setup.updateConfig({ slr: result });
        } catch (error) {
            const message = args.error || "Error saving the DUE configuration";
            notify.error(message);
        }
    };

    return (
        <Page>
            <Page.Header title="DUE" closable onClose={close} />
            <Page.Body maxWidth={1000}>
                <SlrToggle
                    config={config}
                    onChange={(updates) => update(updates)}
                    readonly={!projectManage}
                />
                {config?.enabled && (
                    <>
                        <Header title="L1: Screening" />
                        <Keywords
                            list={config.keywords}
                            onChange={async (keywords) => {
                                await update({ keywords: keywords });
                            }}
                            readonly={readonly}
                        />
                        <Reasons
                            title="Exclusion Reasons (L1)"
                            list={config.l1?.excludeReasons}
                            onChange={(reasons) => {
                                const error =
                                    "Unable to remove the exclusion reason. Please validate the change and make sure it is not used in the project before trying again.";
                                update({ excludeReasons: reasons }, "l1", { error });
                            }}
                            readonly={!projectManage}
                        />

                        <Header title="L2: Article Review" className="mt-25" />
                        <ReviewToggles
                            config={config.l2}
                            onChange={(toggles) => update(toggles, "l2")}
                            readonly={!projectManage}
                        />

                        <Reasons
                            title="Exclusion Reasons (L2)"
                            list={config.l2?.excludeReasons}
                            onChange={(reasons) => {
                                const error =
                                    "Unable to remove the exclusion reason. Please validate the change make sure it is not used in the project before trying again.";
                                update({ excludeReasons: reasons }, "l2", { error });
                            }}
                            readonly={!projectManage}
                        />
                        <AdditionalDataGroup
                            list={config.additionalData}
                            onChange={(additionalData) => update({ additionalData })}
                            readonly={!projectManage}
                            autoGenerate={config.autoGenerate}
                            onAutoGenerateChange={(checked) => update({ autoGenerate: checked })}
                        />

                        <Questions
                            list={config.l2?.questions}
                            useScore={config.l2?.enableScore}
                            onChange={(questions) => update({ questions }, "l2")}
                            readonly={!projectManage}
                        />
                        {config.l2?.enableScore && (
                            <ScoreBands
                                criteria={config.l2.criteria}
                                onChange={(criteria) => update({ criteria }, "l2")}
                                readonly={!projectManage}
                            />
                        )}
                    </>
                )}
            </Page.Body>
        </Page>
    );
});

export default SLR;
